import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useKeyboardViewport } from "../../../design-v2/components/keyboardViewport";
import { countChars, MAX_MESSAGE_CHARS } from "../domain/coachFormat";
import { SendIcon } from "./coachIcons";

/*
 * The message field, docked at the bottom of the page above the tab bar.
 * While the iOS keyboard covers the tab bar, the dock lifts above the keyboard
 * instead (Android resizes the page and keeps the tab bar visible).
 *
 * Enter sends on a desktop keyboard; on a touch screen it adds a line and the
 * Send button sends. Sending clears the field at once; a message the server
 * refused before storing it comes back into the field.
 */

const COUNTER_FROM = MAX_MESSAGE_CHARS - 100;
const MAX_FIELD_HEIGHT_PX = 160;

function prefersSendOnEnter() {
  try {
    return !window.matchMedia?.("(pointer: coarse)")?.matches;
  } catch {
    return true;
  }
}

export default function CoachComposer({ onSend, busy, blocked = null, hint = null, prefill = null }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);
  const fieldRef = useRef(null);
  const keyboard = useKeyboardViewport();
  const count = countChars(value);
  const tooLong = count > MAX_MESSAGE_CHARS;
  const canSend = !blocked && !busy && count > 0 && !tooLong;

  useEffect(() => {
    if (prefill?.text) {
      setValue(prefill.text);
      fieldRef.current?.focus();
    }
  }, [prefill]);

  // Grow with the text up to a few lines, then scroll inside the field.
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, MAX_FIELD_HEIGHT_PX)}px`;
  }, [value]);

  async function submit() {
    if (!canSend) return;
    const text = value;
    setValue("");
    setError(null);
    const result = await onSend(text);
    if (result?.restoreText) setValue((current) => (current ? current : result.restoreText));
    if (result?.message) setError(result.message);
  }

  function handleKeyDown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) return;
    if (!prefersSendOnEnter()) return;
    event.preventDefault();
    submit();
  }

  const dockStyle = keyboard.open && keyboard.inset > 0
    ? { "--lz-coach-dock-bottom": `calc(${keyboard.inset}px + var(--lz-v2-space-2))` }
    : undefined;

  return (
    <div className="lz-coach-dock" style={dockStyle}>
      <div className="lz-coach-dock__inner">
        {blocked ? <div className="lz-coach-notice" role="status">{blocked}</div> : null}
        {!blocked && hint ? <p className="lz-coach-dock__hint">{hint}</p> : null}
        <form
          className="lz-coach-composer"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label htmlFor="lz-coach-message" className="lz-coach-visually-hidden">
            Message your coach
          </label>
          <textarea
            id="lz-coach-message"
            ref={fieldRef}
            className="lz-coach-composer__field"
            rows={1}
            value={value}
            placeholder={blocked ? "Messaging is paused" : "Message your coach"}
            disabled={Boolean(blocked)}
            onChange={(event) => {
              setValue(event.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            aria-invalid={tooLong || Boolean(error) || undefined}
            aria-describedby={error || count >= COUNTER_FROM ? "lz-coach-message-help" : undefined}
            enterKeyHint="send"
          />
          <button
            type="submit"
            className="lz-coach-composer__send"
            aria-label={busy ? "Waiting for the coach" : "Send"}
            disabled={!canSend}
          >
            <SendIcon />
          </button>
        </form>
        {error || count >= COUNTER_FROM ? (
          <p id="lz-coach-message-help" className={`lz-coach-dock__help${tooLong || error ? " is-error" : ""}`}>
            {error || `${count} / ${MAX_MESSAGE_CHARS}`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
